/**
 * personaLayout — iter71 (Item 1)
 *
 * Small pure helpers that turn a persona object (from `case.persona`, may be
 * null) into two things the Dashboard needs:
 *
 *   1. `productOrder(persona)` — an array of product keys in the order the
 *      dashboard should render the product-strip cards. When persona is null
 *      we fall back to a neutral generic order (Manifest, Roam, Packing).
 *   2. `sectionFraming(persona)` — one-liner "what this is for" strings per
 *      product section. These stay static across personas — the reordering
 *      is what carries the personalisation, not the copy. Copy stays honest.
 *
 * The order is derived from real logic, not fabricated. See the mapping below.
 */

export const PRODUCT_KEYS = ["manifest", "roam", "packing"];

// Neutral generic order when persona is missing or skipped.
const NEUTRAL_ORDER = ["manifest", "roam", "packing"];

// Persona-driven ordering. Only the (traveler_type, primary_purpose) pair is
// used to reorder — budget_tier is used elsewhere as a secondary framing hint
// (e.g., surfacing budget tier next to Roam cost lines).
const PURPOSE_ORDER = {
  academic: ["manifest", "packing", "roam"],  // Cohort first for study buddies; Roam last.
  cultural: ["roam",     "manifest", "packing"], // Local exploration up front.
  career:   ["manifest", "roam",     "packing"], // Networking peers first.
  travel:   ["roam",     "manifest", "packing"], // Trip discovery up top.
};

export function productOrder(persona) {
  if (!persona || !persona.primary_purpose) return NEUTRAL_ORDER;
  const base = PURPOSE_ORDER[persona.primary_purpose] || NEUTRAL_ORDER;
  // Budget-conscious travelers should always see Roam BEFORE Packing (Roam
  // surfaces real cost ranges that anchor spending decisions). If Roam already
  // appears earlier than Packing in the purpose-driven base, keep the base
  // ordering — Roam is already prioritised. Otherwise, hoist Roam so it sits
  // immediately before Packing (and after Manifest if Manifest leads).
  if (persona.traveler_type === "budget") {
    const roamIdx = base.indexOf("roam");
    const packIdx = base.indexOf("packing");
    if (roamIdx < packIdx) return base; // already Roam-before-Packing
    // Rebuild with Roam bumped ahead of Packing.
    const without = base.filter((k) => k !== "roam");
    const insertAt = without.indexOf("packing");
    return [...without.slice(0, insertAt), "roam", ...without.slice(insertAt)];
  }
  return base;
}

// Honest, static per-product framing. Uses the persona's primary_purpose to
// slightly reframe copy where it genuinely maps to a different value prop.
// Never fabricates. Falls back to a neutral one-liner when persona is missing.
export function sectionFraming(persona) {
  const p = persona?.primary_purpose;
  return {
    manifest: {
      title: "Who else is going",
      lede: p === "career"
        ? "Match with peers heading to your host university or city. Useful for firms, alumni, and referrals."
        : p === "academic"
          ? "Find peers at your host university to compare course choices and study together."
          : "Match with your cohort. Coordinate arrivals, weekend trips, and roommate hunts.",
    },
    roam: {
      title: "Weekend trips",
      lede: p === "travel"
        ? "Real destinations within ~300 km of your host city, with honest cost ranges."
        : persona?.traveler_type === "budget"
          ? "Weekend trips your budget allows. Real cost ranges, no hidden fees."
          : "3–5 curated destinations near your host city. One tap to plan with peers.",
    },
    packing: {
      title: "What to bring",
      lede: "A personalised cabin, checked-luggage, and arrival shopping list based on your country, dates, airline, and accommodation.",
    },
  };
}

// Compact human-readable summary of the persona, used in the small footer
// "Personalised for you" row so the personalisation is visible + editable.
export function personaSummary(persona) {
  if (!persona) return null;
  const parts = [];
  if (persona.traveler_type)   parts.push(persona.traveler_type === "budget" ? "Budget-conscious" : "Comfort-focused");
  if (persona.primary_purpose) parts.push({
    academic: "academic focus",
    cultural: "cultural immersion",
    career:   "career / networking",
    travel:   "travel-focused",
  }[persona.primary_purpose] || null);
  if (persona.budget_tier) parts.push({ low: "€", mid: "€€", high: "€€€" }[persona.budget_tier] || null);
  return parts.filter(Boolean).join(" · ");
}
